import { totalBookingHours } from '../utils/itemizeBookingReport';
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Building2,
  CalendarCheck,
  DollarSign,
  TrendingUp,
  Plus,
  ArrowRight,
  ExternalLink,
  UserPlus,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { getMyVenues } from '../services/venueService';
import { getVenueBookings } from '../services/bookingService';
import { getOwnerReport } from '../services/reportService';
import { formatCurrency, formatDate, formatTime, getStatusBadgeClass } from '../utils/formatters';
import { getBookingStatusLabel } from '../utils/bookingStatus';

const philippineDateFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: 'Asia/Manila',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

function philippineDateValue() {
  const parts = Object.fromEntries(
    philippineDateFormatter
      .formatToParts(new Date())
      .filter((part) => part.type !== 'literal')
      .map((part) => [part.type, part.value]),
  );
  return `${parts.year}-${parts.month}-${parts.day}`;
}

function addDays(dateValue, amount) {
  const date = new Date(`${dateValue}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + amount);
  return date.toISOString().slice(0, 10);
}

function formatChartDate(value) {
  return new Date(`${value}T00:00:00Z`).toLocaleDateString('en-PH', {
    timeZone: 'UTC',
    month: 'short',
    day: 'numeric',
  });
}

function CapacityTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const day = payload[0].payload;
  return (
    <div className="rounded-xl border border-[#E2E8F0] bg-white p-3 text-xs shadow-lg">
      <p className="mb-2 font-semibold text-[#1F2937]">{formatChartDate(label)}</p>
      {day.hasBreakdown && <>
        <p className="text-[#475569]">Court bookings: {day.courtBookings} · {day.courtBookingHours.toFixed(1)} court-hours</p>
        <p className="mt-1 text-[#475569]">Open Play: {day.openPlaySessions} {day.openPlaySessions === 1 ? 'session' : 'sessions'} · {day.openPlayCourtHours.toFixed(1)} court-hours</p>
      </>}
      <p className="mt-2 border-t border-[#E2E8F0] pt-2 font-semibold text-[#0B7F83]">Total booked: {day.bookedHours.toFixed(1)} court-hours</p>
      <p className="mt-1 text-[#64748B]">Available vacancy: {day.vacantHours.toFixed(1)} court-hours</p>
    </div>
  );
}

export default function Dashboard() {
  const [venues, setVenues] = useState([]);
  const [todayBookings, setTodayBookings] = useState([]);
  const [capacityTrend, setCapacityTrend] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalVenues: 0,
    todayBookingHours: 0,
    todayFeesDeducted: 0,
    utilization: 0,
  });
  const navigate = useNavigate();

  useEffect(() => {
    loadDashboard();
  }, []);

  async function loadDashboard() {
    try {
      const venuesData = await getMyVenues();
      const venuesList = Array.isArray(venuesData) ? venuesData : [];
      setVenues(venuesList);

      const today = philippineDateValue();
      const capacityStart = addDays(today, -7);
      const capacityEnd = addDays(today, 21);
      let totalCourtBookingHours = 0;
      let totalFeesDeducted = 0;
      let totalBookedHours = 0;
      let totalAvailableHours = 0;
      let allBookings = [];

      const venueResults = await Promise.all(venuesList.map(async (venue) => {
        const venueId = venue._id || venue.id;
        try {
          const [bookingsData, report] = await Promise.all([
            getVenueBookings(venueId, { fromDate: today, toDate: today, status: 'active' }),
            getOwnerReport(venueId, capacityStart, capacityEnd),
          ]);
          const bookings = (Array.isArray(bookingsData) ? bookingsData : (bookingsData.bookings || []))
            .filter((booking) => String(booking.status || '').toLowerCase() !== 'cancelled');
          return { venue, bookings, report };
        } catch {
          return null;
        }
      }));

      const capacityByDate = new Map(
        Array.from({ length: 29 }, (_, index) => {
          const date = addDays(capacityStart, index);
          return [date, {
            date,
            bookings: 0,
            courtBookings: 0,
            openPlaySessions: 0,
            bookedHours: 0,
            courtBookingHours: 0,
            openPlayCourtHours: 0,
            availableCourtHours: 0,
            hasBreakdown: true,
          }];
        }),
      );

      venueResults.filter(Boolean).forEach(({ venue, bookings, report }) => {
        const dailyPerformance = report?.dailyPerformance || [];
        const todayPerformance = dailyPerformance.find((day) => day.date === today) || {};
        allBookings = [...allBookings, ...bookings.map((booking) => ({ ...booking, venueName: venue.name }))];
        totalCourtBookingHours += totalBookingHours(bookings.map((booking) => ({ ...booking, bookingType: 'Court Booking' })));
        totalFeesDeducted += Number(todayPerformance.feesDeductedFromPayout || 0);
        totalBookedHours += Number(todayPerformance.bookedHours || 0);
        totalAvailableHours += Number(todayPerformance.availableCourtHours || 0);

        dailyPerformance.forEach((day) => {
          const capacity = capacityByDate.get(day.date);
          if (!capacity) return;
          capacity.bookings += Number(day.bookings || 0);
          capacity.hasBreakdown = capacity.hasBreakdown && day.courtBookings != null &&
            day.openPlaySessions != null && day.courtBookingHours != null && day.openPlayCourtHours != null;
          capacity.courtBookings += Number(day.courtBookings || 0);
          capacity.openPlaySessions += Number(day.openPlaySessions || 0);
          capacity.bookedHours += Number(day.bookedHours || 0);
          capacity.courtBookingHours += Number(day.courtBookingHours || 0);
          capacity.openPlayCourtHours += Number(day.openPlayCourtHours || 0);
          capacity.availableCourtHours += Number(day.availableCourtHours || 0);
        });
      });

      setTodayBookings(allBookings);
      setCapacityTrend([...capacityByDate.values()].map((day) => ({
        ...day,
        vacantHours: Math.max(day.availableCourtHours - day.bookedHours, 0),
      })));
      setStats({
        totalVenues: venuesList.length,
        todayBookingHours: totalCourtBookingHours,
        todayFeesDeducted: totalFeesDeducted,
        utilization: totalAvailableHours > 0 ? Math.round((totalBookedHours / totalAvailableHours) * 100) : 0,
      });
    } catch (err) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#264D59] border-t-transparent" />
      </div>
    );
  }

  const summaryCards = [
    {
      label: 'Total Venues',
      value: stats.totalVenues,
      icon: Building2,
      iconBg: 'bg-[#E8F4FD]',
      iconColor: 'text-[#264D59]',
    },
    {
      label: "Today's Total Booking Hours",
      value: `${stats.todayBookingHours.toLocaleString('en-US', { maximumFractionDigits: 2 })} hrs`,
      icon: CalendarCheck,
      iconBg: 'bg-[#E0F7F4]',
      iconColor: 'text-[#56B7AC]',
    },
    ...(stats.todayFeesDeducted > 0 ? [{
      label: 'Booking Fees Absorbed Today',
      value: formatCurrency(stats.todayFeesDeducted),
      icon: DollarSign,
      iconBg: 'bg-[#FFF8E1]',
      iconColor: 'text-[#B7791F]',
    }] : []),
    {
      label: "Today's Utilization",
      value: `${stats.utilization}%`,
      icon: TrendingUp,
      iconBg: 'bg-[#FFF8E1]',
      iconColor: 'text-[#F9E07F]',
    },
  ];

  const capacityTotals = capacityTrend.reduce((totals, day) => ({
    bookings: totals.bookings + day.bookings,
    courtBookings: totals.courtBookings + day.courtBookings,
    openPlaySessions: totals.openPlaySessions + day.openPlaySessions,
    bookedHours: totals.bookedHours + day.bookedHours,
    vacantHours: totals.vacantHours + day.vacantHours,
    hasBreakdown: totals.hasBreakdown && day.hasBreakdown,
  }), { bookings: 0, courtBookings: 0, openPlaySessions: 0, bookedHours: 0, vacantHours: 0, hasBreakdown: true });

  const quickActions = [
    {
      label: 'Add New Venue',
      desc: 'Create a new FREAC venue',
      icon: Plus,
      iconBg: 'bg-[#E8F4FD]',
      iconColor: 'text-[#264D59]',
      onClick: () => navigate('/venues/new'),
    },
    {
      label: 'Manage Venues',
      desc: 'Edit venues, courts, and availability',
      icon: ExternalLink,
      iconBg: 'bg-[#E0F7F4]',
      iconColor: 'text-[#56B7AC]',
      onClick: () => navigate('/venues'),
    },
    {
      label: 'View Reports',
      desc: 'Analytics and revenue reports',
      icon: TrendingUp,
      iconBg: 'bg-[#FFF8E1]',
      iconColor: 'text-[#F9E07F]',
      onClick: () => navigate('/reports'),
    },
    {
      label: 'Manual Booking',
      desc: 'Create a booking for a walk-in player',
      icon: UserPlus,
      iconBg: 'bg-[#FDF1EB]',
      iconColor: 'text-[#D46C4E]',
      onClick: () => navigate('/bookings/manual'),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1F2937]">Dashboard</h1>
          <p className="mt-1 text-sm text-[#6B7280]">
            Overview of your FREAC venues
          </p>
        </div>
        <button
          onClick={() => navigate('/venues/new')}
          className="btn-primary"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Venue
        </button>
      </div>

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-5">
        {summaryCards.map((card) => (
          <div key={card.label} className="card p-5">
            <div className="flex items-center">
              <div className={`p-3 rounded-lg ${card.iconBg}`}>
                <card.icon className={`h-6 w-6 ${card.iconColor}`} />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-[#6B7280]">{card.label}</p>
                <p className="text-2xl font-semibold text-[#1F2937]">{card.value}</p>
                {card.helper && (
                  <p className="mt-1 text-xs leading-4 text-[#64748B]">{card.helper}</p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <section className="card p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-[#1F2937]">4-Week Court Capacity Outlook</h2>
            <p className="mt-1 text-sm text-[#64748B]">
              Previous 7 days and next 21 days · court bookings and venue-run Open Plays measured in court-hours.
            </p>
          </div>
          <div className="flex flex-wrap gap-2 text-xs">
            {capacityTotals.hasBreakdown ? <>
              <span className="rounded-full bg-[#E8F4FD] px-3 py-1.5 font-semibold text-[#264D59]">{capacityTotals.courtBookings} court bookings</span>
              <span className="rounded-full bg-[#FFF8E1] px-3 py-1.5 font-semibold text-[#8A5A16]">{capacityTotals.openPlaySessions} Open Play {capacityTotals.openPlaySessions === 1 ? 'session' : 'sessions'}</span>
            </> : <span className="rounded-full bg-[#E8F4FD] px-3 py-1.5 font-semibold text-[#264D59]">{capacityTotals.bookings} bookings and Open Plays</span>}
            <span className="rounded-full bg-[#E0F7F4] px-3 py-1.5 font-semibold text-[#0B7F83]">
              {capacityTotals.bookedHours.toFixed(1)} booked hrs
            </span>
            <span className="rounded-full bg-[#F1F5F9] px-3 py-1.5 font-semibold text-[#64748B]">
              {capacityTotals.vacantHours.toFixed(1)} vacant hrs
            </span>
          </div>
        </div>
        <div className="mt-6 h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={capacityTrend} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
              <XAxis dataKey="date" tickFormatter={formatChartDate} minTickGap={22} tick={{ fontSize: 11, fill: '#64748B' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: '#64748B' }} axisLine={false} tickLine={false} unit="h" />
              <Tooltip content={<CapacityTooltip />} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <ReferenceLine x={philippineDateValue()} stroke="#B7791F" strokeDasharray="4 4" label={{ value: 'Today', fill: '#B7791F', fontSize: 11, position: 'insideTopRight' }} />
              <Bar dataKey="bookedHours" name="Booked" stackId="capacity" fill="#0B7F83" />
              <Bar dataKey="vacantHours" name="Available vacancy" stackId="capacity" fill="#CBD5E1" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="card">
          <div className="px-6 py-4 border-b border-[#E2E8F0]">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-[#1F2937]">Today's court bookings</h2>
              <Link
                to="/bookings"
                className="text-sm text-[#264D59] hover:text-[#56B7AC] flex items-center gap-1 transition-colors"
              >
                View all <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          </div>
          <div className="p-6">
            {todayBookings.length === 0 ? (
              <div className="text-center py-8">
                <CalendarCheck className="mx-auto h-12 w-12 text-[#6B7280]" />
                <p className="mt-2 text-sm text-[#6B7280]">No bookings today</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-[#E2E8F0]">
                  <thead>
                    <tr>
                      <th className="text-left text-xs font-medium text-[#6B7280] uppercase tracking-wider pb-3">Player</th>
                      <th className="text-left text-xs font-medium text-[#6B7280] uppercase tracking-wider pb-3">Venue</th>
                      <th className="text-left text-xs font-medium text-[#6B7280] uppercase tracking-wider pb-3">Time</th>
                      <th className="text-left text-xs font-medium text-[#6B7280] uppercase tracking-wider pb-3">Status</th>
                      <th className="text-right text-xs font-medium text-[#6B7280] uppercase tracking-wider pb-3">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E2E8F0]">
                    {todayBookings.slice(0, 5).map((booking) => (
                      <tr
                        key={booking._id || booking.id}
                        className="hover:bg-[#F8FAFC] cursor-pointer"
                        onClick={() => navigate(`/bookings/${booking._id || booking.id}`)}
                      >
                        <td className="py-3 text-sm font-medium text-[#1F2937]">
                          {booking.playerName || booking.player?.name || 'N/A'}
                        </td>
                        <td className="py-3 text-sm text-[#6B7280]">
                          {booking.venueName || 'N/A'}
                        </td>
                        <td className="py-3 text-sm text-[#6B7280]">
                          {formatTime(booking.startTime || booking.start)} - {formatTime(booking.endTime || booking.end)}
                        </td>
                        <td className="py-3">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-[30px] text-xs font-medium ${getStatusBadgeClass(booking.status)}`}>
                            {getBookingStatusLabel(booking.status)}
                          </span>
                        </td>
                        <td className="py-3 text-sm text-[#1F2937] text-right font-medium">
                          {formatCurrency(booking.amount || booking.totalAmount || 0)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        <div className="card">
          <div className="px-6 py-4 border-b border-[#E2E8F0]">
            <h2 className="text-lg font-semibold text-[#1F2937]">Quick Actions</h2>
          </div>
          <div className="p-6 space-y-3">
            {quickActions.map((action) => (
              <button
                key={action.label}
                onClick={action.onClick}
                className="w-full flex items-center justify-between px-4 py-3 rounded-lg border border-[#E2E8F0] hover:border-[#264D59] hover:bg-[#F8FAFC] transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${action.iconBg} ${action.iconColor}`}>
                    <action.icon className="h-5 w-5" />
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-medium text-[#1F2937]">{action.label}</p>
                    <p className="text-xs text-[#6B7280]">{action.desc}</p>
                  </div>
                </div>
                <ArrowRight className="h-4 w-4 text-[#6B7280]" />
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
